import { 
  Organization, 
  UserProfile, 
  ClassRoom, 
  StudentConnection, 
  AcademicYear, 
  Homework, 
  AttendanceRecord, 
  Kudo, 
  OrgFeatureFlags,
  OrgParentPolicies,
  OrgStaffPolicies,
  InvitationCode
} from '../types';
import { 
  MODULE_DEFINITIONS, 
  STAFF_POLICY_DEFINITIONS,
  PARENT_POLICY_DEFINITIONS, 
  FEATURE_PRESETS, 
  isFeatureEnabled, 
  isStaffFeatureEnabled,
  isParentPolicyEnabled 
} from './features';

export interface AssertionResult {
  name: string;
  passed: boolean;
  expected?: any;
  actual?: any;
  error?: string;
}

export interface QATestResult {
  id: string;
  category: 'super_admin' | 'principal' | 'teacher' | 'parent' | 'policies_gating' | 'byok_ai';
  categoryLabel: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  durationMs: number;
  assertions: AssertionResult[];
  error?: string;
}

export interface QASummary {
  total: number;
  passed: number;
  failed: number;
  totalDurationMs: number;
  timestamp: string;
  results: QATestResult[];
}

/**
 * Executes the entire automated in-app QA and Diagnostic Suite in an isolated in-memory sandbox.
 * Zero database writes or production pollution.
 */
export async function runFullQASuite(
  onProgress?: (current: number, total: number, latestResult: QATestResult) => void
): Promise<QASummary> {
  const testDefs: {
    id: string;
    category: QATestResult['category'];
    categoryLabel: string;
    name: string;
    description: string;
    fn: () => Promise<AssertionResult[]> | AssertionResult[];
  }[] = [
    // --- 1. SUPER ADMIN TESTS ---
    {
      id: 'sa-org-verification',
      category: 'super_admin',
      categoryLabel: '👑 Super Admin',
      name: 'School Registration & Verification Transition',
      description: 'Verifies state changes from pending verification to active approval with document metadata.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        const pendingOrg: Organization = {
          id: 'test-org-1',
          name: 'Apex Academy of Science',
          type: 'school',
          adminId: 'principal-1',
          status: 'pending',
          address: { city: 'Boston', state: 'MA', country: 'USA' },
          verification: {
            affiliationNumber: 'BOS-99482',
            documentUrl: 'https://storage.example.com/docs/cert.pdf',
            documentName: 'Gov_Affiliation_2026.pdf'
          }
        };

        assertions.push({
          name: 'Initial pending state is set correctly',
          passed: pendingOrg.status === 'pending' && Boolean(pendingOrg.verification?.documentUrl),
          expected: 'pending with documentUrl',
          actual: `${pendingOrg.status} with ${pendingOrg.verification?.documentUrl}`
        });

        // Simulate Super Admin approval
        const approvedOrg: Organization = {
          ...pendingOrg,
          status: 'active',
          verification: {
            ...pendingOrg.verification,
            reviewedBy: 'super-admin-root',
            reviewedAt: new Date()
          }
        };

        assertions.push({
          name: 'Super Admin approval activates org and attaches review stamp',
          passed: approvedOrg.status === 'active' && Boolean(approvedOrg.verification?.reviewedBy),
          expected: 'active status with reviewedBy',
          actual: `${approvedOrg.status} with ${approvedOrg.verification?.reviewedBy}`
        });

        return assertions;
      }
    },
    {
      id: 'sa-preset-application',
      category: 'super_admin',
      categoryLabel: '👑 Super Admin',
      name: 'Preset Archetype Application & Module Flags',
      description: 'Tests one-click preset application (Lean Starter vs. Full K-12 vs. Coaching Academy).',
      fn: () => {
        const assertions: AssertionResult[] = [];
        
        const leanPreset = FEATURE_PRESETS.find(p => p.id === 'lean_starter');
        const k12Preset = FEATURE_PRESETS.find(p => p.id === 'full_k12');
        const coachingPreset = FEATURE_PRESETS.find(p => p.id === 'coaching_academy');

        assertions.push({
          name: 'All 4 archetype presets are properly registered',
          passed: FEATURE_PRESETS.length === 4,
          expected: 4,
          actual: FEATURE_PRESETS.length
        });

        assertions.push({
          name: 'Lean Starter enables only Core 3 (homework, attendance, announcements)',
          passed: leanPreset?.featureFlags.homework === true &&
                  leanPreset?.featureFlags.attendance === true &&
                  leanPreset?.featureFlags.announcements === true &&
                  leanPreset?.featureFlags.chat === false &&
                  leanPreset?.featureFlags.assessments === false &&
                  leanPreset?.featureFlags.aiTutor === false,
          expected: 'Core 3 true, advanced false',
          actual: JSON.stringify(leanPreset?.featureFlags)
        });

        assertions.push({
          name: 'Full K-12 enables all 11 modules and all parent policies',
          passed: k12Preset?.featureFlags.homework === true &&
                  k12Preset?.featureFlags.chat === true &&
                  k12Preset?.featureFlags.assessments === true &&
                  k12Preset?.featureFlags.aiTutor === true &&
                  k12Preset?.parentPolicies.enableAiTutorForStudents === true,
          expected: 'All true',
          actual: 'All true verified'
        });

        assertions.push({
          name: 'Coaching Academy enables test & AI tutor modules without wellbeing checkins',
          passed: coachingPreset?.featureFlags.assessments === true &&
                  coachingPreset?.featureFlags.aiTutor === true &&
                  coachingPreset?.featureFlags.weeklyCheckin === false,
          expected: 'Assessments: true, AI: true, WeeklyCheckin: false',
          actual: `Assessments: ${coachingPreset?.featureFlags.assessments}, AI: ${coachingPreset?.featureFlags.aiTutor}, Checkin: ${coachingPreset?.featureFlags.weeklyCheckin}`
        });

        return assertions;
      }
    },

    // --- 2. PRINCIPAL TESTS ---
    {
      id: 'pr-class-management',
      category: 'principal',
      categoryLabel: '🏫 Principal',
      name: 'Classroom Creation & Teacher Assignment Matrix',
      description: 'Verifies classroom container isolation, academic year binding, and multi-teacher assignments.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        const academicYear: AcademicYear = {
          id: 'ay-2026',
          name: '2026-2027 Academic Year',
          startDate: '2026-08-01',
          endDate: '2027-06-30',
          orgId: 'test-org-1',
          isActivated: true
        };

        const classroom: ClassRoom = {
          id: 'cls-grade5-a',
          name: 'Grade 5 - Section A',
          orgId: 'test-org-1',
          academicYearId: academicYear.id,
          teacherIds: ['teacher-1', 'teacher-2'],
          classTeacherId: 'teacher-1'
        };

        assertions.push({
          name: 'Classroom is bound to active academic year',
          passed: classroom.academicYearId === academicYear.id && academicYear.isActivated,
          expected: 'ay-2026',
          actual: classroom.academicYearId
        });

        assertions.push({
          name: 'Classroom contains designated Class Teacher and subject teachers',
          passed: classroom.teacherIds.length === 2 && classroom.classTeacherId === 'teacher-1',
          expected: '2 teachers with teacher-1 as lead',
          actual: `${classroom.teacherIds.length} teachers, lead: ${classroom.classTeacherId}`
        });

        return assertions;
      }
    },
    {
      id: 'pr-attendance-metrics',
      category: 'principal',
      categoryLabel: '🏫 Principal',
      name: 'Attendance & Check-in Aggregation Formulae',
      description: 'Tests automated daily attendance percentage calculations across present, late, and absent records.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        const mockRecords: AttendanceRecord[] = [
          { id: '1', studentId: 's1', classId: 'c1', date: '2026-08-30', status: 'present', academicYearId: 'ay-2026', recordedById: 't1' },
          { id: '2', studentId: 's2', classId: 'c1', date: '2026-08-30', status: 'present', academicYearId: 'ay-2026', recordedById: 't1' },
          { id: '3', studentId: 's3', classId: 'c1', date: '2026-08-30', status: 'late', academicYearId: 'ay-2026', recordedById: 't1' },
          { id: '4', studentId: 's4', classId: 'c1', date: '2026-08-30', status: 'absent', academicYearId: 'ay-2026', recordedById: 't1' }
        ];

        const presentOrLateCount = mockRecords.filter(r => r.status === 'present' || r.status === 'late').length;
        const rate = (presentOrLateCount / mockRecords.length) * 100;

        assertions.push({
          name: 'Attendance formula counts Present and Late as attended (3/4 = 75%)',
          passed: rate === 75,
          expected: 75,
          actual: rate
        });

        return assertions;
      }
    },

    // --- 3. TEACHER TESTS ---
    {
      id: 'tc-homework-workflow',
      category: 'teacher',
      categoryLabel: '👩‍🏫 Teacher',
      name: 'Homework Task Decomposition & Attachment Handling',
      description: 'Tests assignment creation with subject breakdown, estimated completion time, and attachments.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        const homework: Homework = {
          id: 'hw-algebra-01',
          classId: 'cls-grade5-a',
          date: '2026-08-30',
          creatorId: 'teacher-1',
          timestamp: new Date(),
          academicYearId: 'ay-2026',
          tasks: [
            { id: 't1', subject: 'Math', description: 'Solve problems 1-10 on page 42', estimatedTime: 20 },
            { id: 't2', subject: 'Science', description: 'Read Chapter 4 on Photosynthesis', estimatedTime: 15 }
          ],
          attachments: ['https://storage.example.com/homework/worksheet1.pdf']
        };

        const totalEstTime = homework.tasks.reduce((sum, t) => sum + (t.estimatedTime || 0), 0);

        assertions.push({
          name: 'Homework contains multiple categorized tasks',
          passed: homework.tasks.length === 2 && homework.tasks[0].subject === 'Math',
          expected: '2 tasks',
          actual: `${homework.tasks.length} tasks`
        });

        assertions.push({
          name: 'Calculates total estimated homework time (35 minutes)',
          passed: totalEstTime === 35,
          expected: 35,
          actual: totalEstTime
        });

        assertions.push({
          name: 'Correctly holds PDF attachment reference',
          passed: homework.attachments?.length === 1 && homework.attachments[0].endsWith('.pdf'),
          expected: '1 PDF attachment',
          actual: homework.attachments?.[0]
        });

        return assertions;
      }
    },
    {
      id: 'tc-kudos-award',
      category: 'teacher',
      categoryLabel: '👩‍🏫 Teacher',
      name: 'Positive Recognition & Kudos Badge Issuance',
      description: 'Verifies awarding badges and positive reinforcement cards to students.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        const kudo: Kudo = {
          id: 'kudo-101',
          studentId: 'student-charlie',
          classId: 'cls-grade5-a',
          academicYearId: 'ay-2026',
          senderId: 'teacher-1',
          senderName: 'Ms. Henderson',
          senderRole: 'teacher',
          type: 'badge',
          badgeId: 'curiosity_champion',
          badgeName: 'Curiosity Champion',
          note: 'Great critical thinking questions during our science lab today!',
          timestamp: new Date()
        };

        assertions.push({
          name: 'Kudo properly records sender role, badge, and encouraging note',
          passed: kudo.senderRole === 'teacher' && kudo.badgeId === 'curiosity_champion' && Boolean(kudo.note),
          expected: 'teacher badge Curiosity Champion',
          actual: `${kudo.senderRole} badge ${kudo.badgeName}`
        });

        return assertions;
      }
    },

    // --- 4. PARENT TESTS ---
    {
      id: 'pr-code-invitation',
      category: 'parent',
      categoryLabel: '👨‍👩‍👧 Parent',
      name: '6-Character Child Invitation Code Validation',
      description: 'Tests child linkage validation, single-use enforcement, and uppercase normalization.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        const mockCode: InvitationCode = {
          id: 'inv-1',
          code: 'EDU849',
          type: 'parent',
          orgId: 'test-org-1',
          classId: 'cls-grade5-a',
          isUsed: false
        };

        // Normalization check
        const userInput = '  edu849 ';
        const normalized = userInput.trim().toUpperCase();

        assertions.push({
          name: 'Code normalization handles whitespace and case-insensitivity',
          passed: normalized === mockCode.code,
          expected: 'EDU849',
          actual: normalized
        });

        // Unused validation check
        const canUse = !mockCode.isUsed && mockCode.type === 'parent';
        assertions.push({
          name: 'Unused parent code is valid for registration',
          passed: canUse === true,
          expected: true,
          actual: canUse
        });

        // Consumed code check
        const consumedCode = { ...mockCode, isUsed: true, usedByUserId: 'parent-1' };
        assertions.push({
          name: 'Consumed code is rejected for subsequent registrations',
          passed: consumedCode.isUsed === true,
          expected: 'isUsed = true',
          actual: `isUsed = ${consumedCode.isUsed}`
        });

        return assertions;
      }
    },
    {
      id: 'pr-homework-checklist',
      category: 'parent',
      categoryLabel: '👨‍👩‍👧 Parent',
      name: 'Interactive Homework Checklist & Completion Progress',
      description: 'Verifies parent/student task completion toggling and percentage calculation.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        const taskIds = ['t1', 't2', 't3', 't4'];
        let completedIds = ['t1', 't2'];

        const progressPercent = (completedIds.length / taskIds.length) * 100;

        assertions.push({
          name: 'Calculates partial homework completion correctly (2/4 = 50%)',
          passed: progressPercent === 50,
          expected: 50,
          actual: progressPercent
        });

        // Toggle t3 on
        completedIds = [...completedIds, 't3'];
        const updatedPercent = (completedIds.length / taskIds.length) * 100;

        assertions.push({
          name: 'Toggling task increments completion (3/4 = 75%)',
          passed: updatedPercent === 75,
          expected: 75,
          actual: updatedPercent
        });

        return assertions;
      }
    },

    // --- 5. POLICIES & GATING TESTS ---
    {
      id: 'pg-feature-gating',
      category: 'policies_gating',
      categoryLabel: '🔒 Policies & Gating',
      name: 'Hierarchical Feature Flag & Parent Policy Cascade Matrix',
      description: 'Tests granular feature gating and ensures parent policies cascade safely to disabled when parent module is off.',
      fn: () => {
        const assertions: AssertionResult[] = [];

        // Org A: Chat is completely disabled at org level
        const orgNoChat: Organization = {
          id: 'org-no-chat',
          name: 'Minimal Academy',
          type: 'school',
          adminId: 'p1',
          address: { city: 'Austin', state: 'TX', country: 'USA' },
          featureFlags: {
            homework: true,
            attendance: true,
            chat: false, // OFF
            aiTutor: false
          },
          parentPolicies: {
            allowParentInitiatedChat: true, // Policy is ON, but feature is OFF
            enableAiTutorForStudents: true  // Policy is ON, but feature is OFF
          }
        };

        // Gating assertions
        const isChatModuleOn = isFeatureEnabled(orgNoChat, 'chat');
        const canParentChat = isParentPolicyEnabled(orgNoChat, 'allowParentInitiatedChat', 'chat');
        const canParentUseAiTutor = isParentPolicyEnabled(orgNoChat, 'enableAiTutorForStudents', 'aiTutor');

        assertions.push({
          name: 'isFeatureEnabled correctly identifies disabled chat module',
          passed: isChatModuleOn === false,
          expected: false,
          actual: isChatModuleOn
        });

        assertions.push({
          name: 'isParentPolicyEnabled cascade blocks parent chat when chat module is OFF',
          passed: canParentChat === false,
          expected: false,
          actual: canParentChat
        });

        assertions.push({
          name: 'isParentPolicyEnabled cascade blocks AI Tutor when aiTutor module is OFF',
          passed: canParentUseAiTutor === false,
          expected: false,
          actual: canParentUseAiTutor
        });

        // Org B: Chat is enabled, but parent policy specifically restricts parent initiation
        const orgChatNoParentInit: Organization = {
          ...orgNoChat,
          featureFlags: {
            ...orgNoChat.featureFlags,
            chat: true
          },
          parentPolicies: {
            ...orgNoChat.parentPolicies,
            allowParentInitiatedChat: false // Teacher-initiated only
          }
        };

        const canParentInit = isParentPolicyEnabled(orgChatNoParentInit, 'allowParentInitiatedChat', 'chat');
        assertions.push({
          name: 'Parent policy toggle successfully restricts parent-initiated chat while module is active',
          passed: canParentInit === false,
          expected: false,
          actual: canParentInit
        });

        return assertions;
      }
    },
    {
      id: 'pg-staff-policy-gating',
      category: 'policies_gating',
      categoryLabel: '🔒 Policies & Gating',
      name: 'Staff & Teacher Hierarchical Policy Gating (e.g. Generate Insight / Assessments)',
      description: 'Verifies that Principal can deactivate specific features for Staff even when Super Admin has enabled the module globally.',
      fn: () => {
        const assertions: AssertionResult[] = [];

        // Org with insights and assessments enabled globally by Super Admin,
        // but Principal turns OFF 'enableTeacherInsights' for staff rollout.
        const orgWithCustomStaffRollout: Organization = {
          id: 'org-staff-rollout',
          name: 'Principal Controlled Academy',
          type: 'school',
          adminId: 'p1',
          address: { city: 'Chicago', state: 'IL', country: 'USA' },
          featureFlags: {
            homework: true,
            attendance: true,
            chat: true,
            insights: true, // Super Admin enabled insights
            assessments: true // Super Admin enabled assessments
          },
          staffPolicies: {
            enableTeacherInsights: false, // Principal deactivated Generate Insight for staff!
            enableTeacherAssessments: true,
            enableTeacherChat: true
          }
        };

        const globalInsightsOn = isFeatureEnabled(orgWithCustomStaffRollout, 'insights');
        const canStaffUseInsights = isStaffFeatureEnabled(orgWithCustomStaffRollout, 'enableTeacherInsights', 'insights');
        const canStaffUseAssessments = isStaffFeatureEnabled(orgWithCustomStaffRollout, 'enableTeacherAssessments', 'assessments');

        assertions.push({
          name: 'Super Admin module entitlement for insights is globally active',
          passed: globalInsightsOn === true,
          expected: true,
          actual: globalInsightsOn
        });

        assertions.push({
          name: 'isStaffFeatureEnabled successfully hides Generate Insight for staff when Principal policy is inactive',
          passed: canStaffUseInsights === false,
          expected: false,
          actual: canStaffUseInsights
        });

        assertions.push({
          name: 'isStaffFeatureEnabled allows staff assessments when both Super Admin module and Principal policy are active',
          passed: canStaffUseAssessments === true,
          expected: true,
          actual: canStaffUseAssessments
        });

        // Scenario 2: Super Admin turns off chat module globally
        const orgGlobalChatOff: Organization = {
          ...orgWithCustomStaffRollout,
          featureFlags: {
            ...orgWithCustomStaffRollout.featureFlags,
            chat: false // Super Admin turned off Chat
          },
          staffPolicies: {
            enableTeacherChat: true // Principal left staff toggle ON
          }
        };

        const canStaffChatWhenGlobalOff = isStaffFeatureEnabled(orgGlobalChatOff, 'enableTeacherChat', 'chat');
        assertions.push({
          name: 'isStaffFeatureEnabled cascade blocks staff chat when Super Admin disabled the global chat module',
          passed: canStaffChatWhenGlobalOff === false,
          expected: false,
          actual: canStaffChatWhenGlobalOff
        });

        return assertions;
      }
    },
    {
      id: 'pg-module-definitions',
      category: 'policies_gating',
      categoryLabel: '🔒 Policies & Gating',
      name: 'Module Definitions & Translation Integrity',
      description: 'Checks all 11 module definitions for valid icons, labels, descriptions, and categories.',
      fn: () => {
        const assertions: AssertionResult[] = [];

        assertions.push({
          name: 'Exactly 11 modules defined in MODULE_DEFINITIONS',
          passed: MODULE_DEFINITIONS.length === 11,
          expected: 11,
          actual: MODULE_DEFINITIONS.length
        });

        const allHaveIconsAndLabels = MODULE_DEFINITIONS.every(
          m => Boolean(m.key) && Boolean(m.label) && Boolean(m.icon) && Boolean(m.description)
        );

        assertions.push({
          name: 'All modules have non-empty keys, labels, icons, and descriptions',
          passed: allHaveIconsAndLabels,
          expected: true,
          actual: allHaveIconsAndLabels
        });

        assertions.push({
          name: 'PARENT_POLICY_DEFINITIONS has 7 granular parent policy controls',
          passed: PARENT_POLICY_DEFINITIONS.length === 7,
          expected: 7,
          actual: PARENT_POLICY_DEFINITIONS.length
        });

        return assertions;
      }
    },
    {
      id: 'pg-supplier-to-school-to-parent-cascade',
      category: 'policies_gating',
      categoryLabel: '🔒 Policies & Gating',
      name: 'Supplier Admin → School Principal → Parent/Teacher Strict Policy Hierarchy',
      description: 'Verifies strict enforcement from Supplier Admin (Feature Flags) down to School Principal (Staff/Parent Policies) down to Parent & Teacher dashboards (Weekly Check-in, Family Connect, Chat Initiation, Kudos).',
      fn: () => {
        const assertions: AssertionResult[] = [];

        // Scenario 1: Supplier Admin disabled weeklyCheckin globally
        const orgSupplierCheckinOff: Organization = {
          id: 'org-supplier-off',
          name: 'Northwood High School',
          type: 'school',
          adminId: 'p-northwood',
          address: { city: 'Seattle', state: 'WA', country: 'USA' },
          featureFlags: {
            weeklyCheckin: false, // Supplier Admin turned OFF weekly checkin
            familyConnect: true,
            chat: true,
            kudos: true
          },
          parentPolicies: {
            enableWeeklyCheckin: true, // School Principal left toggle ON
            enableFamilyConnect: true
          }
        };

        const canShowCheckinSupplierOff = isParentPolicyEnabled(orgSupplierCheckinOff, 'enableWeeklyCheckin', 'weeklyCheckin');
        assertions.push({
          name: 'Supplier Admin module deactivation blocks Weekly Checkin regardless of Principal policy',
          passed: canShowCheckinSupplierOff === false,
          expected: false,
          actual: canShowCheckinSupplierOff
        });

        // Scenario 2: Supplier Admin enabled weeklyCheckin globally, but Principal deactivates Weekly Wellbeing Check-in
        const orgPrincipalCheckinOff: Organization = {
          id: 'org-principal-off',
          name: 'Beacon Hill Academy',
          type: 'school',
          adminId: 'p-beacon',
          address: { city: 'Boston', state: 'MA', country: 'USA' },
          featureFlags: {
            weeklyCheckin: true, // Supplier Admin enabled
            familyConnect: true, // Supplier Admin enabled
            chat: true
          },
          parentPolicies: {
            enableWeeklyCheckin: false, // Principal deactivated!
            enableFamilyConnect: false, // Principal deactivated!
            allowParentInitiatedChat: false // Principal restricted direct 1:1 chat initiation
          }
        };

        const canParentCheckin = isParentPolicyEnabled(orgPrincipalCheckinOff, 'enableWeeklyCheckin', 'weeklyCheckin');
        const canParentCheckinAlias = isParentPolicyEnabled(orgPrincipalCheckinOff, 'allowParentWeeklyCheckin', 'weeklyCheckin');
        const canParentFamilyConnect = isParentPolicyEnabled(orgPrincipalCheckinOff, 'enableFamilyConnect', 'familyConnect');
        const canParentFamilyConnectAlias = isParentPolicyEnabled(orgPrincipalCheckinOff, 'allowParentFamilyConnect', 'familyConnect');
        const canParentInitiateChat = isParentPolicyEnabled(orgPrincipalCheckinOff, 'allowParentInitiatedChat', 'chat');

        assertions.push({
          name: 'Principal policy strictly deactivates Weekly Wellbeing Checkin for parents',
          passed: canParentCheckin === false && canParentCheckinAlias === false,
          expected: false,
          actual: canParentCheckin
        });

        assertions.push({
          name: 'Principal policy strictly deactivates Family Connect daily prompt widget & Character compass for parents',
          passed: canParentFamilyConnect === false && canParentFamilyConnectAlias === false,
          expected: false,
          actual: canParentFamilyConnect
        });

        assertions.push({
          name: 'Principal policy restricts parent-initiated direct chat while preserving class announcements',
          passed: canParentInitiateChat === false,
          expected: false,
          actual: canParentInitiateChat
        });

        // Scenario 3: Principal enables Weekly Checkin and Family Connect
        const orgPrincipalFullyEnabled: Organization = {
          ...orgPrincipalCheckinOff,
          parentPolicies: {
            enableWeeklyCheckin: true,
            enableFamilyConnect: true,
            allowParentInitiatedChat: true
          }
        };

        const canParentCheckinEnabled = isParentPolicyEnabled(orgPrincipalFullyEnabled, 'enableWeeklyCheckin', 'weeklyCheckin');
        const canParentFamilyConnectEnabled = isParentPolicyEnabled(orgPrincipalFullyEnabled, 'enableFamilyConnect', 'familyConnect');
        const canParentChatEnabled = isParentPolicyEnabled(orgPrincipalFullyEnabled, 'allowParentInitiatedChat', 'chat');

        assertions.push({
          name: 'When enabled by Principal, Weekly Wellbeing Checkin is available to parents',
          passed: canParentCheckinEnabled === true,
          expected: true,
          actual: canParentCheckinEnabled
        });

        assertions.push({
          name: 'When enabled by Principal, Family Connect is available to parents',
          passed: canParentFamilyConnectEnabled === true,
          expected: true,
          actual: canParentFamilyConnectEnabled
        });

        assertions.push({
          name: 'When enabled by Principal, Parent 1:1 direct chat is permitted',
          passed: canParentChatEnabled === true,
          expected: true,
          actual: canParentChatEnabled
        });

        return assertions;
      }
    },

    // --- 6. BYOK & AI ENGINE TESTS ---
    {
      id: 'byok-key-routing',
      category: 'byok_ai',
      categoryLabel: '🔑 BYOK & AI Engine',
      name: 'Bring-Your-Own-Key Routing & Fallback Mechanics',
      description: 'Tests active API key assignment when custom key is provided vs. fallback to platform backend when blank.',
      fn: () => {
        const assertions: AssertionResult[] = [];

        const orgWithBYOK: Organization = {
          id: 'org-byok',
          name: 'Stanford International School',
          type: 'school',
          adminId: 'p2',
          address: { city: 'Palo Alto', state: 'CA', country: 'USA' },
          customApiKey: 'AIzaSyA_DEMO_KEY_INSTITUTION_99'
        };

        const orgWithoutBYOK: Organization = {
          id: 'org-standard',
          name: 'Community Elementary',
          type: 'school',
          adminId: 'p3',
          address: { city: 'Denver', state: 'CO', country: 'USA' },
          customApiKey: undefined
        };

        // BYOK Routing logic simulation
        const getKeyForOrg = (org?: Organization) => org?.customApiKey?.trim() || null;

        assertions.push({
          name: 'Routes to institution custom API key when provided',
          passed: getKeyForOrg(orgWithBYOK) === 'AIzaSyA_DEMO_KEY_INSTITUTION_99',
          expected: 'AIzaSyA_DEMO_KEY_INSTITUTION_99',
          actual: getKeyForOrg(orgWithBYOK)
        });

        assertions.push({
          name: 'Returns null (fallback to platform backend) when customApiKey is undefined',
          passed: getKeyForOrg(orgWithoutBYOK) === null,
          expected: null,
          actual: getKeyForOrg(orgWithoutBYOK)
        });

        const orgWithEmptyKey: Organization = { ...orgWithoutBYOK, customApiKey: '   ' };
        assertions.push({
          name: 'Trims whitespace and falls back to null when customApiKey is blank string',
          passed: getKeyForOrg(orgWithEmptyKey) === null,
          expected: null,
          actual: getKeyForOrg(orgWithEmptyKey)
        });

        return assertions;
      }
    },
    {
      id: 'byok-socratic-prompt',
      category: 'byok_ai',
      categoryLabel: '🔑 BYOK & AI Engine',
      name: 'Socratic AI Tutor Prompt & Safety Constraint Validation',
      description: 'Validates that the AI Tutor system prompt enforces pedagogy: hints only, no answers, age-appropriate steps.',
      fn: () => {
        const assertions: AssertionResult[] = [];
        
        // Simulating the prompt generator logic
        const generateTutorSystemInstruction = (subject: string, language: string) => {
          return `You are a warm, encouraging Socratic tutor helping a student with ${subject}. 
Do NOT give away the final answer directly. Ask guiding questions, give hints, and break down steps in ${language}.`;
        };

        const prompt = generateTutorSystemInstruction('Mathematics (Fractions)', 'English');

        assertions.push({
          name: 'Prompt contains strict anti-spoiler pedagogical directive',
          passed: prompt.includes('Do NOT give away the final answer directly'),
          expected: 'Contains anti-spoiler directive',
          actual: 'Contains anti-spoiler directive verified'
        });

        assertions.push({
          name: 'Prompt adapts dynamically to homework subject and language',
          passed: prompt.includes('Mathematics (Fractions)') && prompt.includes('in English'),
          expected: 'Subject and language interpolated',
          actual: 'Subject and language verified'
        });

        return assertions;
      }
    }
  ];

  const results: QATestResult[] = [];
  const startTime = Date.now();

  for (let i = 0; i < testDefs.length; i++) {
    const def = testDefs[i];
    const testStartTime = Date.now();
    let assertions: AssertionResult[] = [];
    let status: QATestResult['status'] = 'passed';
    let error: string | undefined;

    try {
      assertions = await def.fn();
      const hasFailedAssertion = assertions.some(a => !a.passed);
      if (hasFailedAssertion) {
        status = 'failed';
      }
    } catch (e: any) {
      status = 'failed';
      error = e.message || 'Execution error';
      assertions.push({
        name: 'Unhandled runtime error in test execution',
        passed: false,
        error: e.message
      });
    }

    const testDurationMs = Date.now() - testStartTime;
    const testResult: QATestResult = {
      id: def.id,
      category: def.category,
      categoryLabel: def.categoryLabel,
      name: def.name,
      description: def.description,
      status,
      durationMs: testDurationMs,
      assertions,
      error
    };

    results.push(testResult);
    if (onProgress) {
      onProgress(i + 1, testDefs.length, testResult);
    }
  }

  const passedCount = results.filter(r => r.status === 'passed').length;
  const failedCount = results.filter(r => r.status === 'failed').length;

  return {
    total: results.length,
    passed: passedCount,
    failed: failedCount,
    totalDurationMs: Date.now() - startTime,
    timestamp: new Date().toISOString(),
    results
  };
}
